import { useState, useRef, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

// Lazy import for Capacitor plugin to prevent iOS freeze on import
let CapacitorSpeechRecognition: any = null;

const getCapacitorSpeechRecognition = async () => {
  if (!CapacitorSpeechRecognition && Capacitor.isNativePlatform()) {
    try {
      const module = await import('@capacitor-community/speech-recognition');
      CapacitorSpeechRecognition = module.SpeechRecognition;
    } catch (e) {
      console.error('Failed to load speech recognition module:', e);
    }
  }
  return CapacitorSpeechRecognition;
};

interface UseLiveTranscriptionProps {
  onTranscriptionUpdate: (text: string) => void;
  language?: string;
}

export const useLiveTranscription = ({
  onTranscriptionUpdate, 
  language = 'en' 
}: UseLiveTranscriptionProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [accumulatedText, setAccumulatedText] = useState('');
  const streamRef = useRef<MediaStream | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const nativeListenerRef = useRef<boolean>(false);
  const isStartingRef = useRef<boolean>(false);
  const { toast } = useToast();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (nativeListenerRef.current && CapacitorSpeechRecognition) {
        try {
          CapacitorSpeechRecognition.removeAllListeners();
          CapacitorSpeechRecognition.stop();
        } catch (e) {
          console.log('Cleanup error:', e);
        }
      }
    };
  }, []);

  // Preload the native speech recognition plugin on iOS/Android so the first tap feels responsive
  // (and so permission prompts are triggered from the user's gesture with less delay).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    void getCapacitorSpeechRecognition();
  }, []);

  const getSupportedMimeType = (): string => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/wav'
    ];
    
    for (const type of types) {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return 'audio/webm'; // fallback
  };

  const initializeGladiaSession = async (): Promise<string | null> => {
    try {
      console.log('Attempting to initialize Gladia session via edge function...');
      
      // Call our secure edge function instead of Gladia directly
      const { data, error } = await supabase.functions.invoke('gladia-live-session', {
        body: {
          encoding: 'wav/pcm',
          sample_rate: 16000,
          bit_depth: 16,
          channels: 1,
        },
      });

      if (error) {
        console.error("Edge function error:", error);
        throw new Error(`Edge function error: ${error.message}`);
      }

      if (!data?.url) {
        console.error("No WebSocket URL returned from edge function");
        throw new Error("No WebSocket URL returned");
      }

      console.log('Gladia session initialized successfully via edge function');
      
      return data.url;
    } catch (error) {
      console.error('Error initializing Gladia session:', error);
      return null;
    }
  };

  const setupWebSocket = (url: string) => {
    const ws = new WebSocket(url);
    websocketRef.current = ws;

    ws.addEventListener('open', () => {
      console.log('WebSocket connected to Gladia');
    });

    ws.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'transcript') {
          const utterance = message.data?.utterance;
          if (utterance?.text && message.data?.is_final) {
            const startTime = formatTime(utterance.start || 0);
            const newText = `${startTime}: ${utterance.text}`;
            
            setAccumulatedText(prev => {
              const updatedText = prev + (prev ? '\n' : '') + newText;
              onTranscriptionUpdate(updatedText);
              return updatedText;
            });
          } else if (utterance?.text && !message.data?.is_final) {
            // Show partial results for real-time feedback
            const startTime = formatTime(utterance.start || 0);
            const partialText = `${startTime}: ${utterance.text}`;
            
            // Don't add to accumulated text, just show for feedback
            onTranscriptionUpdate(accumulatedText + (accumulatedText ? '\n' : '') + partialText + ' ...');
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });

    ws.addEventListener('error', (error) => {
      console.error('WebSocket error:', error);
      toast({
        title: "Connection Error",
        description: "Lost connection to transcription service",
        variant: "destructive",
      });
    });

    ws.addEventListener('close', (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      if (event.code !== 1000 && isRecording) {
        toast({
          title: "Connection Lost",
          description: "Transcription service disconnected",
          variant: "destructive",
        });
      }
    });
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const startNativeRecording = useCallback(async () => {
    // Prevent multiple simultaneous starts
    if (isStartingRef.current) {
      console.log('iOS: Already starting, ignoring...');
      return;
    }

    isStartingRef.current = true;

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const safeCall = async <T,>(
      label: string,
      fn: () => Promise<T>,
      timeoutMs: number
    ): Promise<T | null> => {
      try {
        const result = await Promise.race([
          fn(),
          new Promise<T>((_, reject) =>
            window.setTimeout(() => reject(new Error(`${label} timeout`)), timeoutMs)
          ),
        ]);
        return result;
      } catch (e) {
        console.log(`iOS: ${label} failed:`, e);
        return null;
      }
    };

    const safeVoidCall = async (
      label: string,
      fn: () => Promise<unknown>,
      timeoutMs: number
    ): Promise<boolean> => {
      const result = await safeCall(label, fn, timeoutMs);
      return result !== null;
    };

    try {
      console.log('iOS: Starting native recording...');

      // Load the plugin lazily to prevent freeze on import
      const SpeechPlugin = await getCapacitorSpeechRecognition();
      if (!SpeechPlugin) {
        toast({
          title: 'Not Available',
          description: 'Speech recognition plugin could not be loaded.',
          variant: 'destructive',
        });
        return;
      }

      // Clean up any existing state first (never await native calls here; they can hang on some iOS builds)
      if (nativeListenerRef.current) {
        console.log('iOS: Cleaning up previous session...');
        nativeListenerRef.current = false;

        try {
          SpeechPlugin.removeAllListeners();
        } catch (e) {
          console.log('Pre-cleanup listeners:', e);
        }

        await sleep(80);

        try {
          SpeechPlugin.stop();
        } catch (e) {
          console.log('Pre-cleanup stop:', e);
        }

        await sleep(120);
      }

      // Check availability (guard with timeout)
      console.log('iOS: Checking availability...');
      let available = true;
      const availability = await safeCall<{ available: boolean }>(
        'available()',
        () => SpeechPlugin.available(),
        2500
      );
      if (availability && typeof availability.available === 'boolean') {
        available = availability.available;
      }

      if (!available) {
        toast({
          title: 'Not Supported',
          description: 'Speech recognition is not available on this device.',
          variant: 'destructive',
        });
        return;
      }

      // Request permissions - MUST await the actual result, no fallback
      // This is critical: iOS won't show the app in Settings until permission is actually requested
      console.log('iOS: Requesting permissions (will show native dialog)...');
      
      let permissionStatus: any = null;
      try {
        // Call requestPermissions directly without timeout fallback
        // The native dialog MUST appear for iOS to register the app in Settings
        permissionStatus = await SpeechPlugin.requestPermissions();
        console.log('iOS permission result:', permissionStatus);
      } catch (permError) {
        console.error('iOS permission request error:', permError);
        toast({
          title: 'Permission Error',
          description: 'Could not request permissions. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      if (permissionStatus?.speechRecognition === 'denied') {
        toast({
          title: 'Permission Denied',
          description: 'Please enable Speech Recognition in iOS Settings > Privacy > Speech Recognition.',
          variant: 'destructive',
        });
        return;
      }
      
      // Also check if we got prompt status (user hasn't decided yet)
      if (permissionStatus?.speechRecognition === 'prompt') {
        console.log('iOS: Permission prompt shown, waiting for user decision...');
        // Re-request to get the actual result after user responds
        try {
          permissionStatus = await SpeechPlugin.requestPermissions();
          console.log('iOS permission after prompt:', permissionStatus);
        } catch (e) {
          console.log('iOS: Second permission request failed:', e);
        }
        
        if (permissionStatus?.speechRecognition === 'denied') {
          toast({
            title: 'Permission Denied',
            description: 'Speech recognition permission is required.',
            variant: 'destructive',
          });
          return;
        }
      }

      // Trigger iOS microphone permission prompt explicitly so the "Microphone" toggle appears in Settings.
      if (Capacitor.getPlatform() === 'ios' && navigator?.mediaDevices?.getUserMedia) {
        try {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          micStream.getTracks().forEach((t) => t.stop());
        } catch (e) {
          console.log('iOS: getUserMedia mic permission failed:', e);
        }
      }

      setAccumulatedText('');

      // Map language code for iOS
      const iosLanguage = language.replace('-', '_');
      console.log('iOS: Using language:', iosLanguage);

      // Listener confirmation (avoids the "nothing happens" scenario if start() hangs)
      let resolveStarted: ((started: boolean) => void) | null = null;
      const startedPromise = new Promise<boolean>((resolve) => {
        resolveStarted = resolve;
      });
      const startedTimeout = window.setTimeout(() => resolveStarted?.(false), 3500);

      nativeListenerRef.current = true;

      // IMPORTANT: addListener can hang in some iOS edge cases, so never block on it.
      void safeCall(
        'addListener(listeningState)',
        () =>
          SpeechPlugin.addListener('listeningState', (data: { status: 'started' | 'stopped' }) => {
            console.log('iOS: listeningState:', data);
            if (data.status === 'started') resolveStarted?.(true);
            if (data.status === 'stopped') setIsRecording(false);
          }),
        1500
      );

      void safeCall(
        'addListener(partialResults)',
        () =>
          SpeechPlugin.addListener('partialResults', (data: { matches: string[] }) => {
            console.log('iOS: Got partial results:', data);
            if (data.matches && data.matches.length > 0) {
              const text = data.matches[0];
              const timestamp = formatTime((Date.now() / 1000) % 3600);
              const formattedText = `${timestamp}: ${text}`;

              setAccumulatedText((prev) => {
                const updatedText = prev ? `${prev}\n${formattedText}` : formattedText;
                onTranscriptionUpdate(updatedText);
                return updatedText;
              });
            }
          }),
        1500
      );

      await sleep(60);

      console.log('iOS: Starting recognition...');

      const startResolvedPromise = safeVoidCall('start()', () =>
        SpeechPlugin.start({
          language: iosLanguage,
          partialResults: true,
          popup: false,
        }), 3500);

      const started = await Promise.race([
        startedPromise,
        startResolvedPromise,
        new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 4500)),
      ]);

      window.clearTimeout(startedTimeout);

      if (!started) {
        nativeListenerRef.current = false;

        try {
          SpeechPlugin.removeAllListeners();
        } catch {}
        try {
          SpeechPlugin.stop();
        } catch {}

        toast({
          title: 'Could Not Start',
          description: 'Speech recognition failed to start. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      setIsRecording(true);

      console.log('iOS: Recording started successfully');
      toast({
        title: 'Recording Started',
        description: 'Speak now...',
      });
    } catch (error: any) {
      console.error('iOS native recording error:', error);
      nativeListenerRef.current = false;

      const SpeechPlugin = await getCapacitorSpeechRecognition();
      if (SpeechPlugin) {
        try {
          SpeechPlugin.removeAllListeners();
        } catch {}
        try {
          SpeechPlugin.stop();
        } catch {}
      }

      toast({
        title: 'Recording Error',
        description: error?.message || 'Could not start. Please try again.',
        variant: 'destructive',
      });
    } finally {
      isStartingRef.current = false;
    }
  }, [language, onTranscriptionUpdate, toast]);

  const stopNativeRecording = useCallback(async () => {
    console.log('iOS: Stopping native recording...');
    nativeListenerRef.current = false;
    
    const SpeechPlugin = await getCapacitorSpeechRecognition();
    if (SpeechPlugin) {
      // Remove listeners first - non-blocking
      try {
        SpeechPlugin.removeAllListeners();
      } catch (e) {
        console.log('Remove listeners error:', e);
      }
      
      // Small delay
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Stop with fire-and-forget to prevent freeze
      try {
        SpeechPlugin.stop();
      } catch (e) {
        console.log('Stop error:', e);
      }
    }
    
    setIsRecording(false);
    
    toast({
      title: "Recording Stopped",
      description: "Speech recognition has ended",
    });
  }, [toast]);

  const startWebRecording = useCallback(async () => {
    console.log('startWebRecording: Starting (platform:', Capacitor.getPlatform(), ')');
    
    try {
      // IMPORTANT (iOS WKWebView): getUserMedia + AudioContext must be created/resumed
      // directly from the user gesture. If we await network calls first, iOS can refuse
      // to show the permission prompt and the start button appears unresponsive.

      // Request microphone permission - this should trigger the iOS permission dialog
      console.log('startWebRecording: Requesting microphone access via getUserMedia...');
      let stream: MediaStream;

      // Get user media
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 16000
          } 
        });
        console.log('startWebRecording: Got media stream:', stream.id);
      } catch (mediaError: any) {
        console.error('startWebRecording: getUserMedia failed:', mediaError.name, mediaError.message);
        throw mediaError;
      }
      
      streamRef.current = stream;
      setAccumulatedText('');

      // Setup audio processing
      const audioContext = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      // iOS can start AudioContext in a suspended state; resume while we still have gesture context.
      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume();
        } catch (e) {
          console.warn('startWebRecording: audioContext.resume() failed:', e);
        }
      }
      
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event) => {
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          const inputData = event.inputBuffer.getChannelData(0);
          const outputData = new Int16Array(inputData.length);
          
          // Convert float32 to int16
          for (let i = 0; i < inputData.length; i++) {
            outputData[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
          }
          
          // Send audio data as binary
          websocketRef.current.send(outputData.buffer);
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      // Initialize Gladia WebSocket session (network) AFTER mic permission is granted.
      console.log('startWebRecording: Initializing Gladia session...');
      const websocketUrl = await initializeGladiaSession();
      if (!websocketUrl) {
        console.error('startWebRecording: Failed to get websocket URL');
        toast({
          title: "Session Error",
          description: "Could not initialize transcription session",
          variant: "destructive",
        });

        // Cleanup mic resources since we already opened the stream.
        if (processorRef.current) {
          try {
            processorRef.current.disconnect();
          } catch {}
          processorRef.current = null;
        }
        if (audioContextRef.current) {
          try {
            await audioContextRef.current.close();
          } catch {}
          audioContextRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
        return;
      }
      console.log('startWebRecording: Got websocket URL:', websocketUrl);

      // Setup WebSocket connection
      setupWebSocket(websocketUrl);

      setIsRecording(true);

      toast({
        title: "Recording Started",
        description: "Live transcription is now active",
      });

    } catch (error: any) {
      console.error('Error starting web recording:', error);

      // Ensure we don't leave the mic open if we fail mid-start.
      try {
        if (websocketRef.current) {
          websocketRef.current.close(1000);
          websocketRef.current = null;
        }
        if (processorRef.current) {
          processorRef.current.disconnect();
          processorRef.current = null;
        }
        if (audioContextRef.current) {
          await audioContextRef.current.close();
          audioContextRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      } catch {}
      
      let errorMessage = "Could not access microphone. Please check permissions.";
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = "Microphone permission denied. Please enable it in your browser settings.";
      } else if (error.name === 'NotFoundError') {
        errorMessage = "No microphone found on your device.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Recording Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [onTranscriptionUpdate, toast]);

  const stopWebRecording = useCallback(() => {
    // Stop WebSocket
    if (websocketRef.current) {
      websocketRef.current.send(JSON.stringify({ type: "stop_recording" }));
      websocketRef.current.close(1000);
      websocketRef.current = null;
    }

    // Stop audio processing
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    setIsRecording(false);
    
    toast({
      title: "Recording Stopped",
      description: "Live transcription has ended",
    });
  }, [toast]);

  // Platform-aware start/stop
  // iOS: Use Gladia API (via web recording with getUserMedia) instead of unreliable native plugin
  // Android: Use native speech recognition plugin
  // Web: Use Gladia API
  const startRecording = useCallback(async () => {
    const platform = Capacitor.getPlatform();
    console.log('startRecording called, platform:', platform);
    
    if (platform === 'android') {
      // Android uses native speech recognition
      console.log('startRecording: Using native recording for Android');
      await startNativeRecording();
    } else {
      // iOS and Web both use Gladia API via getUserMedia
      console.log('startRecording: Using web recording (Gladia) for', platform);
      await startWebRecording();
    }
  }, [startNativeRecording, startWebRecording]);

  const stopRecording = useCallback(() => {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'android') {
      stopNativeRecording();
    } else {
      // iOS and Web both use Gladia API
      stopWebRecording();
    }
  }, [stopNativeRecording, stopWebRecording]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    accumulatedText
  };
};